import type { OperationKey } from '@foodlista/operations'

export interface TrackedOperation {
  operationId: string
}

export const TRACKING_BY_SCOPE_KEY = 'scoped-operation-tracking'
export const PENDING_BY_SCOPE_KEY = 'scoped-operation-pending'

export function serializeScope(scope: Record<string, unknown>): string {
  return Object.entries(scope)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value ?? ''}`)
    .join('&')
}

export function scopeCacheKey(
  key: OperationKey,
  scope: Record<string, unknown>,
): string {
  return `${key}:${serializeScope(scope)}`
}

export function useScopedOperationTracking() {
  return useState<Record<string, TrackedOperation | undefined>>(
    TRACKING_BY_SCOPE_KEY,
    () => ({}),
  )
}

export function useScopedOperationPending() {
  return useState<Record<string, boolean>>(PENDING_BY_SCOPE_KEY, () => ({}))
}

export function useScopedOperationActive(
  key: OperationKey,
  scope: MaybeRefOrGetter<Record<string, unknown>>,
) {
  const trackingByScope = useScopedOperationTracking()
  const pendingByScope = useScopedOperationPending()
  const cacheKey = computed(() => scopeCacheKey(key, toValue(scope)))

  return computed(() => {
    const id = cacheKey.value
    if (pendingByScope.value[id]) {
      return true
    }

    return !!trackingByScope.value[id]?.operationId
  })
}

export function setScopedOperationPending(
  pendingByScope: Ref<Record<string, boolean>>,
  cacheKey: string,
  pending: boolean,
) {
  const next = { ...pendingByScope.value }
  if (pending) {
    next[cacheKey] = true
  } else {
    delete next[cacheKey]
  }
  pendingByScope.value = next
}

export function setScopedOperationTracking(
  trackingByScope: Ref<Record<string, TrackedOperation | undefined>>,
  cacheKey: string,
  tracking: TrackedOperation | null,
) {
  const next = { ...trackingByScope.value }
  if (tracking) {
    next[cacheKey] = tracking
  } else {
    delete next[cacheKey]
  }
  trackingByScope.value = next
}
